import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, InputBase, IconButton, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { Link } from 'react-router-dom';
import { fetchNews, fetchNewsDates, fetchSavedNewsIds, saveNews, unsaveNews } from '../lib/api';
import { getCurrentUser } from '../lib/auth';
import NewsCard from './NewsCard';

const extractDateKey = (ymd) => {
  const m = String(ymd || '').match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const formatYMDJapanese = (key) => {
  if (!key) return '日付不明';
  const [year, month, day] = key.split('-').map(Number);
  if (!year || !month || !day) return '日付不明';
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const dow = weekdays[new Date(year, month - 1, day).getDay()];
  return `${year}年${month}月${day}日（${dow}）`;
};

const groupByYMD = (articles) => {
  const groups = {};
  for (const article of articles) {
    const key = extractDateKey(article.YMD) || '0000-00-00';
    if (!groups[key]) groups[key] = [];
    groups[key].push(article);
  }
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
};

const getDateStr = (offsetDays = 0) => {
  const d = new Date(Date.now() - offsetDays * 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CACHE_KEY = 'glainews_dates_v1';

const CATEGORY_SITES = {
  News: ['BBC', 'TECHCRUNCH', 'ARSTECHNICA', 'AINEWS'],
  Tech: ['LANGCHAIN', 'AWS', 'ANTHROPIC', 'GOOGLE', 'IEEE', 'OPENAI'],
  SNS:  ['DIGG'],
};

const CategoryFilter = ({ selected, onChange }) => {
  const tabs = ['All', 'News', 'Tech', 'SNS'];
  return (
    <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
      {tabs.map(tab => (
        <Box
          key={tab}
          onClick={() => onChange(tab === 'All' ? null : tab)}
          sx={{
            px: 1.5, py: 0.4, cursor: 'pointer', borderRadius: 1,
            fontSize: '0.72rem', fontFamily: '"Courier New", Courier, monospace',
            fontWeight: selected === (tab === 'All' ? null : tab) ? 700 : 400,
            color: selected === (tab === 'All' ? null : tab) ? '#ea580c' : '#78716c',
            border: '1px solid',
            borderColor: selected === (tab === 'All' ? null : tab) ? '#ea580c' : '#ccc8c2',
            backgroundColor: selected === (tab === 'All' ? null : tab) ? '#fff7f0' : 'transparent',
            '&:hover': { borderColor: '#ea580c', color: '#ea580c' },
            transition: 'all 0.15s',
          }}
        >
          {tab}
        </Box>
      ))}
    </Box>
  );
};

const SidebarSourceItem = ({ name, count, isSelected, onClick }) => (
  <Box
    onClick={() => onClick(name)}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 0.75,
      px: 1,
      cursor: 'pointer',
      borderRadius: 1,
      '&:hover': { backgroundColor: '#ddd9d2' },
      backgroundColor: isSelected ? '#ddd9d2' : 'transparent',
    }}
  >
    <Typography sx={{ fontSize: '0.78rem', fontFamily: 'inherit', color: '#1c1916', fontWeight: isSelected ? 700 : 400 }}>
      {name}
    </Typography>
    <Typography sx={{ fontSize: '0.72rem', fontFamily: 'inherit', color: '#a8a29e' }}>
      {count}
    </Typography>
  </Box>
);

const NewsList = () => {
  const currentUser = getCurrentUser();
  const currentUserEmail = currentUser?.email || null;

  // Date accordion mode
  const [dateList, setDateList] = useState([]);
  const [newsPerDate, setNewsPerDate] = useState({});
  const [loadingDates, setLoadingDates] = useState({});
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [listLoading, setListLoading] = useState(true);

  // Filter mode
  const [news, setNews] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(0);

  // Shared
  const [filteredCategory, setFilteredCategory] = useState(null);
  const [filteredSite, setFilteredSite] = useState(null);
  const [savedNewsIds, setSavedNewsIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 10;
  const hasInitialized = useRef(false);
  const activeFilter = filteredSite || searchTerm;

  const fetchDateArticles = async (date) => {
    setLoadingDates(prev => ({ ...prev, [date]: true }));
    try {
      const articles = await fetchNews(0, 500, null, null, date);
      setNewsPerDate(prev => ({ ...prev, [date]: articles }));
    } catch (e) {
      console.error(`${date} の記事取得に失敗:`, e);
    } finally {
      setLoadingDates(prev => ({ ...prev, [date]: false }));
    }
  };

  const loadDateList = async () => {
    setListLoading(true);
    try {
      const dates = await fetchNewsDates();
      setDateList(dates);
      const today = getDateStr(0);
      const yesterday = getDateStr(1);
      setExpandedDates(new Set([today, yesterday]));
      for (const { date } of dates) {
        if (date === today || date === yesterday) {
          fetchDateArticles(date);
        }
      }
    } catch (e) {
      console.error('日付一覧の取得に失敗:', e);
    } finally {
      setListLoading(false);
    }
  };

  const loadFilteredNews = async (pageNum, reset = false) => {
    setFilterLoading(true);
    try {
      const data = await fetchNews(pageNum, limit, filteredSite, searchTerm);
      setNews(prev => {
        if (reset) return data;
        return [...prev, ...data.filter(a => !prev.some(p => p.id === a.id))];
      });
      setPage(pageNum);
    } catch (e) {
      console.error('ニュースの取得に失敗:', e);
    } finally {
      setFilterLoading(false);
    }
  };

  // Load saved news IDs on mount
  useEffect(() => {
    fetchSavedNewsIds().then(ids => setSavedNewsIds(ids));
  }, []);

  // Main data loading (mount + filter changes)
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const data = JSON.parse(cached);
          if (data.dateList && data.dateList.length > 0) {
            setDateList(data.dateList);
            setNewsPerDate(data.newsPerDate || {});
            setExpandedDates(new Set(data.expandedArr || []));
            setListLoading(false);
            return;
          }
        }
      } catch {}
      loadDateList();
      return;
    }
    if (filteredSite || searchTerm) {
      loadFilteredNews(0, true);
    } else {
      loadDateList();
    }
  }, [filteredSite, searchTerm]);

  // Cache date mode state for browser back
  useEffect(() => {
    if (!activeFilter && dateList.length > 0) {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        dateList,
        newsPerDate,
        expandedArr: Array.from(expandedDates),
      }));
    }
  }, [newsPerDate, expandedDates, dateList]);

  // Scroll restore
  useEffect(() => {
    const savedY = sessionStorage.getItem(CACHE_KEY + '_scroll');
    if (!savedY || listLoading || filterLoading) return;
    window.scrollTo(0, parseInt(savedY, 10));
    sessionStorage.removeItem(CACHE_KEY + '_scroll');
  }, [listLoading, filterLoading]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem(CACHE_KEY + '_scroll', String(window.scrollY));
    };
  }, []);

  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
        if (!newsPerDate[date] && !loadingDates[date]) {
          fetchDateArticles(date);
        }
      }
      return next;
    });
  };

  const filterBySite = (siteName) => {
    setFilteredSite(prev => prev === siteName ? null : siteName);
    setSearchTerm('');
    setSearchQuery('');
  };

  const handleSearch = () => {
    setFilteredSite(null);
    setSearchTerm(searchQuery);
  };

  const clearAll = () => {
    setFilteredSite(null);
    setSearchTerm('');
    setSearchQuery('');
  };

  const filterArticlesByCategory = (articles) => {
    if (!filteredCategory) return articles;
    const sites = CATEGORY_SITES[filteredCategory] || [];
    return articles.filter(a => sites.includes(a.SITE_NAME));
  };

  const handleToggleSave = async (article) => {
    const isAlreadySaved = savedNewsIds.includes(article.id);
    try {
      if (isAlreadySaved) {
        await unsaveNews(article.id);
        setSavedNewsIds(prev => prev.filter(id => id !== article.id));
      } else {
        await saveNews(article.id);
        setSavedNewsIds(prev => [...prev, article.id]);
      }
    } catch (err) {
      console.error('保存/解除に失敗:', err);
    }
  };

  const allLoadedArticles = activeFilter ? news : Object.values(newsPerDate).flat();
  const sourceCounts = allLoadedArticles.reduce((acc, a) => {
    const s = a.SITE_NAME || 'OTHER';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const sortedSources = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);

  const topArticle = !activeFilter ? (newsPerDate[getDateStr(0)] || [])[0] : null;

  return (
    <Box sx={{ maxWidth: 1160, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
      {/* Highlights */}
      {topArticle && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.82rem', fontFamily: 'inherit', color: '#78716c', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Highlights 🌐
          </Typography>
          <Box sx={{ border: '1px solid #ccc8c2', borderRadius: 1, p: 2, maxWidth: 380, backgroundColor: '#e8e4de' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <WhatshotIcon sx={{ fontSize: 13, color: '#ea580c' }} />
              <Typography sx={{ fontSize: '0.65rem', fontFamily: 'inherit', color: '#ea580c', fontWeight: 700, letterSpacing: '0.06em' }}>
                IN CASE YOU MISSED IT
              </Typography>
            </Box>
            <Typography
              component={Link}
              to={`/article/${topArticle.id}`}
              sx={{ display: 'block', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.4, color: '#1c1916', textDecoration: 'none', fontFamily: 'inherit', '&:hover': { color: '#ea580c' } }}
            >
              {topArticle.TITLE}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: { xs: 0, md: 5 }, alignItems: 'flex-start' }}>
        {/* Main feed */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              {activeFilter && (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: 'inherit', color: '#1c1916' }}>
                    {filteredSite || `"${searchTerm}"`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={clearAll}
                    startIcon={<CloseIcon sx={{ fontSize: 11 }} />}
                    sx={{ fontSize: '0.7rem', fontFamily: 'inherit', color: '#78716c', p: 0, minWidth: 0, '&:hover': { color: '#ea580c', backgroundColor: 'transparent' } }}
                  >
                    クリア
                  </Button>
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', mt: 0.5 }}>
              <Box sx={{ flex: 1 }} />
              <Box
                component="form"
                onSubmit={e => { e.preventDefault(); handleSearch(); }}
                sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc8c2', borderRadius: 1, px: 1, py: 0.2, backgroundColor: '#e8e4de', '&:focus-within': { borderColor: '#a8a29e' } }}
              >
                <InputBase
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  sx={{ fontSize: '0.75rem', fontFamily: '"Courier New", Courier, monospace', color: '#1c1916', width: 130, '& input::placeholder': { color: '#b8b4ae' } }}
                />
                <IconButton type="submit" size="small" sx={{ p: 0.25, color: '#b8b4ae', '&:hover': { color: '#78716c' } }}>
                  <SearchIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <CategoryFilter selected={filteredCategory} onChange={setFilteredCategory} />

          <Divider sx={{ borderColor: '#ccc8c2' }} />

          {/* Date accordion mode */}
          {!activeFilter && (
            listLoading ? (
              <Typography sx={{ color: '#a8a29e', fontSize: '0.82rem', fontFamily: 'inherit', py: 4 }}>読み込み中...</Typography>
            ) : (
              dateList.map(({ date, count }) => {
                const isExpanded = expandedDates.has(date);
                const articles = filterArticlesByCategory(newsPerDate[date] || []);
                const isLoadingThis = loadingDates[date];
                return (
                  <Box key={date}>
                    <Box
                      onClick={() => toggleDate(date)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 3,
                        pl: 1.5,
                        py: 0.75,
                        borderLeft: '3px solid #ea580c',
                        borderBottom: '1px solid #ccc8c2',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { backgroundColor: '#f0ede8' },
                      }}
                    >
                      <Typography sx={{ flex: 1, fontSize: '0.95rem', fontWeight: 700, color: '#1c1916', fontFamily: 'inherit' }}>
                        {formatYMDJapanese(date)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#a8a29e', fontFamily: 'inherit', mr: 1 }}>
                        {count}件
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#b8b4ae', mr: 0.5, lineHeight: 1 }}>
                        {isExpanded ? '▲' : '▼'}
                      </Typography>
                    </Box>
                    {isExpanded && (
                      isLoadingThis ? (
                        <Typography sx={{ color: '#a8a29e', fontSize: '0.82rem', fontFamily: 'inherit', py: 3, pl: 2 }}>
                          読み込み中...
                        </Typography>
                      ) : (
                        articles.map((article, i) => (
                          <NewsCard
                            key={article.id}
                            article={article}
                            index={i + 1}
                            selectedSite={filteredSite}
                            onFilterBySite={filterBySite}
                            isSaved={savedNewsIds.includes(article.id)}
                            onSaveArticle={handleToggleSave}
                            currentUserEmail={currentUserEmail}
                          />
                        ))
                      )
                    )}
                  </Box>
                );
              })
            )
          )}

          {/* Filter mode */}
          {activeFilter && (
            filterLoading ? (
              <Typography sx={{ color: '#a8a29e', fontSize: '0.82rem', fontFamily: 'inherit', py: 4 }}>読み込み中...</Typography>
            ) : (
              <>
                {groupByYMD(news).map(([ymdKey, articles]) => (
                  <Box key={ymdKey}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, pl: 1.5, py: 0.75, borderLeft: '3px solid #ea580c', borderBottom: '1px solid #ccc8c2' }}>
                      <Typography sx={{ flex: 1, fontSize: '0.95rem', fontWeight: 700, color: '#1c1916', fontFamily: 'inherit' }}>
                        {formatYMDJapanese(ymdKey)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#a8a29e', fontFamily: 'inherit' }}>
                        {articles.length}件
                      </Typography>
                    </Box>
                    {articles.map((article, i) => (
                      <NewsCard
                        key={article.id}
                        article={article}
                        index={i + 1}
                        selectedSite={filteredSite}
                        onFilterBySite={filterBySite}
                        isSaved={savedNewsIds.includes(article.id)}
                        onSaveArticle={handleToggleSave}
                        currentUserEmail={currentUserEmail}
                      />
                    ))}
                  </Box>
                ))}
                <Box sx={{ pt: 2, textAlign: 'center' }}>
                  <Button
                    onClick={() => loadFilteredNews(page + 1)}
                    sx={{ fontSize: '0.75rem', fontFamily: 'inherit', color: '#78716c', border: '1px solid #ccc8c2', borderRadius: 1, px: 3, py: 0.6, '&:hover': { borderColor: '#a8a29e', color: '#1c1916', backgroundColor: 'transparent' } }}
                  >
                    [ load more ]
                  </Button>
                </Box>
              </>
            )
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: 220, flexShrink: 0, display: { xs: 'none', md: 'block' }, pt: 0.5 }}>
          <Typography sx={{ fontSize: '0.7rem', fontFamily: 'inherit', fontWeight: 700, color: '#a8a29e', letterSpacing: '0.08em', mb: 1 }}>
            SOURCES
          </Typography>
          <Box sx={{ border: '1px solid #ccc8c2', borderRadius: 1, overflow: 'hidden', backgroundColor: '#e8e4de' }}>
            {sortedSources.slice(0, 12).map(([name, count]) => (
              <SidebarSourceItem key={name} name={name} count={count} isSelected={filteredSite === name} onClick={filterBySite} />
            ))}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontSize: '0.7rem', fontFamily: 'inherit', fontWeight: 700, color: '#a8a29e', letterSpacing: '0.08em', mb: 1 }}>
              AI RANKINGS
            </Typography>
            <Box
              component={Link}
              to="/ranking"
              sx={{ display: 'block', border: '1px solid #ccc8c2', borderRadius: 1, p: 1.5, backgroundColor: '#e8e4de', textDecoration: 'none', '&:hover': { backgroundColor: '#ddd9d2' } }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontFamily: 'inherit', color: '#1c1916', fontWeight: 700 }}>Chatbot Arena →</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontFamily: 'inherit', color: '#78716c', mt: 0.5 }}>AIモデルのリアルタイムランキング</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NewsList;
